import { useQuery } from '@tanstack/react-query';

import { Page } from '@/components/layout/Page';
import { linkify } from '@/pages/Overview/entities';

/* ── Colors ───────────────────────────────────────────────────────────── */
const C = {
  blueDark: '#274483',
  t1: '#1A1C2E',
  t2: '#5C5F72',
  t3: '#9295A5',
  border: '#E4E0D8',
  warm: '#F5F6F8',
  white: '#FFFFFF',
  pos: '#2A8055',
  neg: '#BC2E3D',
  warn: '#D27A24',
};
const tone = (c?: string) =>
  c === 'positive'
    ? C.pos
    : c === 'warning'
      ? C.warn
      : c === 'negative'
        ? C.neg
        : c === 'muted'
          ? C.t3
          : c === 'text2'
            ? C.t2
            : c === 'blueDark'
              ? C.blueDark
              : C.t1;
const toneBg = (c?: string) =>
  c === 'positive'
    ? 'rgba(42,128,85,.1)'
    : c === 'warning'
      ? 'rgba(210,122,36,.1)'
      : c === 'negative'
        ? 'rgba(188,46,61,.1)'
        : 'rgba(39,68,131,.08)';

/* ── Types ────────────────────────────────────────────────────────────── */
type Financial = { label: string; value: string; sub: string; badge: string; badgeColor: string };
type Decision = { name: string; sub: string; predicted: string; actual: string; outcome: string; outcomeColor: string };
type ByCustomer = { name: string; pct: number; barColor: string };
type Trend = { label: string; pct: string; sub: string; subColor: string; highlight: boolean };
type SummaryRow = { l: string; v: string; vColor?: string };
type SummaryCol = {
  quarter: string;
  status: string;
  statusColor: string;
  quarterColor: string;
  opacity: number;
  rows: SummaryRow[];
};
type PerfData = {
  title: string;
  sub: string;
  context: string;
  tabs: string[];
  financial: Financial[];
  decisions: Decision[];
  accuracy: { byCustomer: ByCustomer[]; trend: Trend[]; note: string };
  summary: SummaryCol[];
};

const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: 8 };
const eyebrow: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: C.t3,
  textTransform: 'uppercase',
  letterSpacing: '.08em',
};
const cellHead: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: C.t3,
  textTransform: 'uppercase',
  letterSpacing: '.08em',
};
const COLS = '1fr 72px 72px 92px';

export function Performance() {
  const { data } = useQuery<PerfData>({
    queryKey: ['overview', 'performance'],
    queryFn: () => fetch('/api/overview/performance').then((r) => r.json()),
  });

  return (
    <Page
      title={data?.title ?? 'Performance'}
      subheader={data?.sub}
      actions={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            alignSelf: 'flex-start',
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 11, color: C.t3 }}>{data?.context}</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {(data?.tabs ?? []).map((t, i) => (
              <button
                key={t}
                type="button"
                style={{
                  fontSize: 10,
                  fontWeight: i === 0 ? 700 : 600,
                  padding: '3px 10px',
                  borderRadius: 100,
                  border: `1.5px solid ${i === 0 ? C.blueDark : C.border}`,
                  background: i === 0 ? C.blueDark : 'transparent',
                  color: i === 0 ? 'white' : C.t2,
                  cursor: 'pointer',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3.5">
        {/* Financial impact strip */}
        <div className="grid grid-cols-4 gap-3">
          {(data?.financial ?? []).map((f) => (
            <div key={f.label} style={{ ...card, padding: '14px 16px' }}>
              <div style={{ ...eyebrow, marginBottom: 8 }}>{f.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: C.t1, letterSpacing: '-0.03em', marginBottom: 4 }}>
                {f.value}
              </div>
              <div style={{ fontSize: 10, color: C.t3, marginBottom: 6 }}>{f.sub}</div>
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 100,
                  background: toneBg(f.badgeColor),
                  color: tone(f.badgeColor),
                }}
              >
                {f.badge}
              </span>
            </div>
          ))}
        </div>

        {/* Decision outcomes + Forecast accuracy */}
        <div className="grid grid-cols-2 gap-3">
          {/* Decision Outcomes */}
          <div style={{ ...card, overflow: 'hidden' }}>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Decision Outcomes</div>
              <span style={{ fontSize: 9.5, color: C.t3 }}>Recommended → Actioned → Result</span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: COLS,
                padding: '7px 16px',
                borderBottom: `1px solid ${C.border}`,
                background: C.warm,
              }}
            >
              <div style={cellHead}>Decision</div>
              <div style={{ ...cellHead, textAlign: 'center' }}>Predicted</div>
              <div style={{ ...cellHead, textAlign: 'center' }}>Actual</div>
              <div style={{ ...cellHead, textAlign: 'right' }}>Outcome</div>
            </div>
            {(data?.decisions ?? []).map((d, i, arr) => (
              <div
                key={d.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  padding: '10px 16px',
                  borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 1 }}>{linkify(d.name)}</div>
                  <div style={{ fontSize: 9.5, color: C.t3 }}>{d.sub}</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t2, textAlign: 'center' }}>{d.predicted}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t2, textAlign: 'center' }}>{d.actual}</div>
                <div style={{ textAlign: 'right' }}>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 100,
                      background: toneBg(d.outcomeColor),
                      color: tone(d.outcomeColor),
                    }}
                  >
                    {d.outcome}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Forecast Accuracy */}
          <div style={{ ...card, overflow: 'hidden' }}>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Forecast Accuracy</div>
              <span style={{ fontSize: 9.5, color: C.t3 }}>MAPE · trailing 3 cycles</span>
            </div>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ ...eyebrow, marginBottom: 10 }}>By Customer</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(data?.accuracy.byCustomer ?? []).map((c) => (
                  <div
                    key={c.name}
                    style={{ display: 'grid', gridTemplateColumns: '110px 1fr 40px', gap: 8, alignItems: 'center' }}
                  >
                    <div style={{ fontSize: 10.5, color: C.t2 }}>{linkify(c.name)}</div>
                    <div style={{ height: 4, background: C.border, borderRadius: 2 }}>
                      <div style={{ height: 4, background: tone(c.barColor), borderRadius: 2, width: `${c.pct}%` }} />
                    </div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: C.t1, textAlign: 'right' }}>{c.pct}%</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ ...eyebrow, marginBottom: 10 }}>Cycle-over-Cycle · Weighted MAPE</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                {(data?.accuracy.trend ?? []).map((t) => (
                  <div
                    key={t.label}
                    style={{
                      textAlign: 'center',
                      padding: 10,
                      borderRadius: 4,
                      background: t.highlight ? 'rgba(39,68,131,.05)' : C.warm,
                      border: t.highlight ? '1px solid rgba(39,68,131,.12)' : '1px solid transparent',
                    }}
                  >
                    <div style={{ fontSize: 9, color: C.t3, marginBottom: 4 }}>{t.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: t.highlight ? C.blueDark : C.t1 }}>{t.pct}</div>
                    <div
                      style={{
                        fontSize: 9,
                        color: tone(t.subColor),
                        marginTop: 2,
                        fontWeight: t.subColor === 'positive' ? 700 : 400,
                      }}
                    >
                      {t.sub}
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: 10,
                  padding: '9px 12px',
                  background: 'rgba(42,128,85,.05)',
                  borderRadius: 4,
                  borderLeft: `3px solid ${C.pos}`,
                }}
              >
                <div style={{ fontSize: 10.5, color: C.t2, lineHeight: 1.5 }}>{linkify(data?.accuracy.note)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Cycle summary */}
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Cycle Summary · Keystone Impact</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)' }}>
            {(data?.summary ?? []).map((col, i, arr) => (
              <div
                key={col.quarter}
                style={{
                  padding: '16px 18px',
                  borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
                  opacity: col.opacity,
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: tone(col.quarterColor) }}>{col.quarter}</div>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 100,
                      background: toneBg(col.statusColor),
                      color: tone(col.statusColor),
                    }}
                  >
                    {col.status}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {col.rows.map((r) => (
                    <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 10.5, color: C.t3 }}>{r.l}</span>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: tone(r.vColor) }}>{r.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}
